import { Box, Button, Icon, Typography } from "@outschool/backpack";
import { EnrollmentMeetingPurchaseQueryQuery } from "@outschool/gql-frontend-generated";
import { faPlus } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
// This file is not yet translated.
/* eslint-disable i18next/no-literal-string */
import React from "react";

import { ActivityEnrollQueryQuery } from "../../../../../shared/generated/graphql";
import LearnerEditableItem from "./LearnerEditableItem";
import LearnerItem from "./LearnerItem";
import { LearnerItemType, defaultLearnerItem, uncheck } from "./utils";

export * from "./utils";

type LearnerItemsProps = {
  learnerItems: LearnerItemType[];
  onLearnerItemsUpdate(learnerItems: LearnerItemType[]): void;
  disabled: boolean;
  highlightErrors: boolean;
  ignoreStrictAgeRange: boolean;
  activity: // @ts-ignore TS(2339): Property 'activity' does not exist on type '{ __ty... Remove this comment to see the full error message
  | ActivityEnrollQueryQuery["enrollmentForm"]["activity"]
    // @ts-ignore TS(2339): Property 'activity' does not exist on type '{ __ty... Remove this comment to see the full error message
    | EnrollmentMeetingPurchaseQueryQuery["enrollmentMeetingPurchaseForm"]["activity"];
  buyingLearners?: LearnerItemType["learner"][];
  requestAgeRangeException: (arg0: LearnerItemType) => void;
  clearErrors?: () => void;
  onlyAllowSingleLearner?: boolean;
};

export default function LearnerItems({
  disabled,
  highlightErrors,
  learnerItems,
  onLearnerItemsUpdate,
  activity,
  buyingLearners,
  requestAgeRangeException,
  ignoreStrictAgeRange,
  clearErrors,
  onlyAllowSingleLearner = false,
}: LearnerItemsProps) {
  const { t } = useTranslation(
    "client\\components\\enrolling\\enrollmentForm\\learnerItems\\LearnerItemCheckboxes",
  );
  function handleAdd() {
    const updatedLearners = onlyAllowSingleLearner
      ? [...uncheck(learnerItems), defaultLearnerItem()] // defaultLearnerItem() returns a checked learner item. for ClassWallet we only want one checked learner at a time, so we need to uncheck the other learners.
      : [...learnerItems, defaultLearnerItem()];
    onLearnerItemsUpdate(updatedLearners);
  }

  function handleUpdate(learnerItem: LearnerItemType) {
    // If the class only allows for selecting a single learner, uncheck any other learners when making a selection
    if (onlyAllowSingleLearner) {
      const updatedLearners = learnerItems.map(li => {
        if (li.key === learnerItem.key) {
          return learnerItem;
        }
        return { ...li, checked: false };
      });
      onLearnerItemsUpdate(updatedLearners);
    } else {
      const updatedLearners = learnerItems.map(li =>
        li.key !== learnerItem.key ? li : learnerItem,
      );
      onLearnerItemsUpdate(updatedLearners);
    }
    // clear errors when deselecting + selecting a new learner
    clearErrors?.();
  }

  const hasSelectedOneLearner = buyingLearners?.length === 1;
  return (
    <React.Fragment>
      {learnerItems
        .filter(li => !!li.learner.uid)
        .map(li =>
          // bulk create accounts can have learners without age
          li.hasEmptyName || li.learner.age === null ? (
            <LearnerEditableItem
              disabled={disabled}
              key={li.key}
              learnerItem={li}
              highlightErrors={highlightErrors}
              onUpdate={handleUpdate}
              activity={activity}
              ignoreStrictAgeRange={ignoreStrictAgeRange}
              requestAgeRangeException={requestAgeRangeException}
            />
          ) : (
            <LearnerItem
              disabled={disabled}
              key={li.key}
              learnerItem={li}
              onUpdate={handleUpdate}
              activity={activity}
              ignoreStrictAgeRange={ignoreStrictAgeRange}
              requestAgeRangeException={requestAgeRangeException}
            />
          ),
        )}
      {learnerItems
        .filter(li => !li.learner.uid)
        .map(li => (
          <LearnerEditableItem
            disabled={
              onlyAllowSingleLearner && hasSelectedOneLearner
                ? disabled || !li.checked
                : disabled
            }
            key={li.key}
            learnerItem={li}
            highlightErrors={highlightErrors}
            onUpdate={handleUpdate}
            activity={activity}
            ignoreStrictAgeRange={ignoreStrictAgeRange}
            requestAgeRangeException={requestAgeRangeException}
          />
        ))}
      <Box
        sx={{
          marginTop: "0.5em",
        }}
      >
        {disabled ? (
          <Typography variant="inherit" sx={{ fontSize: "1.4rem" }}>
            <Icon className="fas" icon={faPlus} /> {t("Add Another Learner")}
          </Typography>
        ) : (
          <Button
            variant="link"
            sx={{
              alignSelf: "start",
            }}
            onClick={() => handleAdd()}
          >
            <Typography variant="inherit" sx={{ fontSize: "1.4rem" }}>
              <Icon icon={faPlus} /> {t("Add Another Learner")}
            </Typography>
          </Button>
        )}
      </Box>
    </React.Fragment>
  );
}
